export const base64ToUrl = (data: string) =>
  data.replace(/\//g, '_').replace(/\+/g, '-');

export const urlToBase64 = (data: string) =>
  data.replace(/_/g, '/').replace(/-/g, '+');

// see: https://stackoverflow.com/questions/30106476/using-javascripts-atob-to-decode-base64-doesnt-properly-decode-utf-8-strings
// make sure to only use base64ToUint8Array on strings produced from uint8ArrayToBase64
// Uint8Array -> UTF-8 -> percent encoded UTF-8 -> binary -> base64
export const uint8ArrayToBase64 = (data: Uint8Array): string => {
  const utf8String = data.reduce((acc, n) => acc + String.fromCharCode(n), '');
  const percentEncodedUtf8String = encodeURIComponent(utf8String);
  const binaryString = percentEncodedUtf8String.replace(
    /%([0-9A-F]{2})/g,
    (_, p1) => String.fromCharCode(parseInt(p1, 16))
  );
  // eslint-disable-next-line deprecation/deprecation
  const base64Encoded = btoa(binaryString);

  return base64Encoded;
};

export const base64ToUint8Array = (base64Encoded: string): Uint8Array => {
  // eslint-disable-next-line deprecation/deprecation
  const binaryString = atob(base64Encoded);
  const percentEncodedUtf8String = Array.prototype.map
    .call(
      binaryString,
      (char: string) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`
    )
    .join('');
  const utf8String = decodeURIComponent(percentEncodedUtf8String);
  const data = utf8String.split('').map((char) => char.charCodeAt(0));

  return Uint8Array.from(data);
};

export const uint8ArrayToUrl = (data: Uint8Array) =>
  base64ToUrl(uint8ArrayToBase64(data));

export const urlToUint8Array = (base64Encoded: string) =>
  base64ToUint8Array(urlToBase64(base64Encoded));

/**
 * Binary-safe base64 encoding. Unlike `uint8ArrayToBase64`, this does NOT use
 * `encodeURIComponent` and is therefore safe for arbitrary binary data such as
 * Y.js state updates that may contain bytes > 0x7F.
 */
export const binaryToBase64 = (data: Uint8Array): string => {
  // Process in chunks to avoid call-stack overflow on large arrays
  const CHUNK_SIZE = 0x8000;
  const parts: string[] = [];
  for (let i = 0; i < data.length; i += CHUNK_SIZE) {
    const slice = data.subarray(i, i + CHUNK_SIZE);
    parts.push(String.fromCharCode(...slice));
  }
  // eslint-disable-next-line deprecation/deprecation
  return btoa(parts.join(''));
};

/**
 * Binary-safe base64 decoding. Counterpart of `binaryToBase64`.
 */
export const base64ToBinary = (b64: string): Uint8Array => {
  // eslint-disable-next-line deprecation/deprecation
  const raw = atob(b64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    bytes[i] = raw.charCodeAt(i);
  }
  return bytes;
};

/**
 * Binary-safe URL-safe base64 encoding.
 */
export const binaryToUrlBase64 = (data: Uint8Array): string =>
  base64ToUrl(binaryToBase64(data));

/**
 * Binary-safe URL-safe base64 decoding.
 */
export const urlBase64ToBinary = (encoded: string): Uint8Array =>
  base64ToBinary(urlToBase64(encoded));
